import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { OrganizationSchema, WebPageSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import useSEO from '@/hooks/useSEO';
import { stats } from '@/mocks/home';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useSEO({
    title: '关于我们',
    description: '亚洲在线品牌介绍。亚洲数字生活一站式平台，覆盖娱乐、资讯、购物、社交等多元场景。了解我们的品牌故事、企业信誉与联系方式。',
    keywords: '亚洲在线关于我们,亚洲在线品牌,亚洲在线联系方式,亚洲数字生活平台',
    canonicalUrl: 'https://taokep.cn/about',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { name: '首页', url: 'https://taokep.cn/' },
    { name: '关于我们', url: 'https://taokep.cn/about' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const phoneAlt = formData.get('phone_alt') as string;
    if (phoneAlt && phoneAlt.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9tvv85ca23r0tr1anpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <OrganizationSchema />
      <WebPageSchema
        url="https://taokep.cn/about"
        name="关于我们 - 亚洲在线"
        description="了解亚洲在线的品牌故事、企业信誉与联系方式"
        breadcrumbs={breadcrumbItems}
      />
      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />
      <main>
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20Asian%20digital%20office%20with%20diverse%20team%20collaborating%2C%20warm%20natural%20light%20from%20large%20windows%2C%20minimalist%20workspace%20design%2C%20creative%20atmosphere%2C%20professional%20corporate%20photography&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="关于亚洲在线"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/65"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
              关于亚洲在线
            </h1>
            <p className="text-sm md:text-base text-background-100/70 max-w-lg mx-auto">
              连接亚洲，点亮数字生活
            </p>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70" aria-label="Breadcrumb">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-900 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
                  品牌介绍
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  亚洲数字生活一站式平台
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    亚洲在线成立于2024年，总部位于新加坡，在北京、东京、首尔、吉隆坡设有区域运营中心。
                    作为亚洲领先的综合数字服务平台，我们致力于为亚洲12个国家和地区的用户提供
                    涵盖<strong>娱乐</strong>、<strong>资讯</strong>、<strong>购物</strong>、<strong>社交</strong>等多元场景的一站式在线体验。
                  </p>
                  <p>
                    我们拥有超过<strong>5000万注册用户</strong>，支持8种亚洲主要语言。平台汇聚了
                    来自亚洲各国的优质内容创作者、品牌商家和社区伙伴，每天为数千万用户提供个性化的数字生活服务。
                  </p>
                  <p>
                    亚洲在线的使命是打破地理和语言边界，让每一位亚洲用户无论身处何地，
                    都能享受流畅、安全、本地化的数字生活体验。我们的愿景是成为亚洲最值得信赖的数字生活平台。
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">{stat.number}</div>
                    <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium mb-4">
                企业信誉
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                值得信赖的数字平台
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                我们用专业和诚信赢得每一位用户的信任
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '数据安全', desc: '银行级256位SSL加密，通过ISO 27001信息安全认证' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '7x24小时多语言客服，用户满意度达98.7%' },
                { icon: 'ri-award-line', title: '行业认可', desc: '2026年亚洲数字创新奖、最佳用户体验平台' },
                { icon: 'ri-hand-heart-line', title: '社会责任', desc: '每年投入利润的3%用于亚洲数字素养教育项目' },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                与我们取得联系
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <form
                  id="contact-form"
                  data-readdy-form="contact"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入咨询主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>

                {formStatus === 'success' && (
                  <div className="mt-4 p-4 rounded-lg bg-accent-50 text-accent-800 text-sm">
                    <i className="ri-checkbox-circle-line mr-1.5"></i>
                    感谢您的留言！我们会在1-2个工作日内回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-4 rounded-lg bg-red-50 text-red-700 text-sm">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError || '提交失败，请稍后重试'}
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-map-pin-line', label: '总部地址', value: '新加坡滨海湾金融中心 Tower 3, #42-01' },
                      { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@taokep.cn' },
                      { icon: 'ri-phone-line', label: '客服热线', value: '+65 6789 0000（周一至周日 9:00-21:00）' },
                      { icon: 'ri-wechat-line', label: '微信公众号', value: '亚洲在线AsiaOnline' },
                    ].map((item) => (
                      <div key={item.label} className="flex gap-4">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-secondary-50 text-secondary-600">
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500">{item.label}</div>
                          <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl overflow-hidden border border-background-200/70">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.804588957275!2d103.850764!3d1.285203!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19ae7ef6e4ff%3A0x30f8a9c495d2d68e!2sMarina%20Bay%20Financial%20Centre!5e0!3m2!1szh-CN!2ssg!4v1692000000000!5m2!1szh-CN!2ssg"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="亚洲在线总部位置"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}